import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import type { ContainerSample, ContainerBottle } from './SampleTransferPillPopups';
// Reuse Registration's Containers chrome (viewbar tabs, split layout, section headers)
// so the two screens look identical (standing rule 9). The classes live here.
import './sampleRegistration/sampleRegistration.css';

/**
 * Sample Transfer ▸ Containers — Sample Transfer's OWN, BOTTLE-AWARE view (owner
 * request 2026-09-29). Unlike Registration's shared ContainersTab (one container /
 * preservative / storage PER TEST), this shows ONE ROW PER BOTTLE, so two bottles of
 * the same test can carry DIFFERENT storage — exactly what the transfer receipt holds
 * (e.g. Adjusted Gross Alpha: bottle A → Cooler #2, bottle B → Cooler #1). Registration's
 * per-test grid collapsed those two into one row and hid bottle B's storage.
 *
 * Fed by GET /sample-transfer/{job}/containers (which already returns one row per
 * dbo.SampleBottleAllocation) and edited through Sample Transfer's OWN receipt-safe,
 * per-bottle PUT /sample-transfer/bottle/{allocationOid} — never Registration's job-wide
 * save (which wipes the receipt columns). "Only the UI is shared; the API is separate."
 */

const API = '/api/v1/sample-transfer';
/** The empty (null-FK) option, shown and picked as "N/A" (matches the legacy popup). */
const NA = 'N/A';

/** '' (no FK) renders as "N/A" so a blank storage/preservative reads clearly. */
function naIfBlank(v: string): string { return v ? v : NA; }

export function SampleTransferContainers({ jobOid }: { jobOid: string; jobId: string }) {
  const [samples, setSamples] = useState<ContainerSample[]>([]);
  const [bottles, setBottles] = useState<ContainerBottle[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'bysample' | 'all'>('bysample');
  const [activeSample, setActiveSample] = useState<string | null>(null);  // the SampleId string

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`${API}/${jobOid}/containers`)
      .then(r => {
        const d = (r.data as { data: { samples: ContainerSample[]; bottles: ContainerBottle[] } }).data;
        setSamples(d?.samples ?? []);
        // Blank FK values → "N/A" so the editable combos have a matching selected option.
        setBottles((d?.bottles ?? []).map(b => ({
          ...b,
          container: naIfBlank(b.container), preservative: naIfBlank(b.preservative),
          storageId: naIfBlank(b.storageId), storageCondition: naIfBlank(b.storageCondition),
        })));
        setActiveSample(prev => prev ?? (d?.samples?.[0]?.sampleId ?? null));
      })
      .catch(() => { setSamples([]); setBottles([]); })
      .finally(() => setLoading(false));
  }, [jobOid]);
  useEffect(() => { void load(); }, [load]);

  // Bottle columns, one row per bottle (A, B, …). READ-ONLY — legacy Sample Transfer's
  // Containers popup is a display-only master-detail viewer (no Save, no editable cells,
  // no Copy/Reset Bottles — owner report 2026-09-30).
  const editCols: AppColumn<ContainerBottle>[] = [
    { field: 'test', header: 'Test', width: 170 },
    { field: 'bottleId', header: 'BottleID', width: 90 },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'container', header: 'Containers', width: 160 },
    { field: 'preservative', header: 'Preservative', width: 130 },
    { field: 'storageId', header: 'StorageID', width: 130 },
    { field: 'storageCondition', header: 'StorageCondition', width: 150 },
  ];

  // Master grid — the active row is highlighted by AppDataGrid's selectedRowId
  // (keyed on the row Oid), exactly like Registration's Sample Details grid.
  const sampleCols: AppColumn<ContainerSample>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150,
      render: s => <span style={{ fontWeight: 600 }}>{s.sampleId}</span>, text: s => s.sampleId },
    { field: 'qty', header: 'Qty', width: 60, align: 'right', type: 'number', text: s => String(s.qty ?? '') },
  ];

  const activeOid = useMemo(
    () => samples.find(s => s.sampleId === activeSample)?.oid ?? null,
    [samples, activeSample]);

  const rightRows = useMemo(
    () => (activeSample ? bottles.filter(b => b.sampleId === activeSample) : []),
    [bottles, activeSample]);

  // All-in-One: SampleID (bold) then the same bottle columns.
  const allCols: AppColumn<ContainerBottle>[] = [
    { field: 'sampleId', header: 'SampleID', width: 150,
      render: b => <span style={{ fontWeight: 600 }}>{b.sampleId}</span>, text: b => b.sampleId },
    ...editCols,
  ];

  return (
    <div className="stx-pop-body">
      {/* View-toggle only — All in One / By Sample. Copy / Reset Bottles removed: the
          legacy Containers popup is a read-only viewer (owner report 2026-09-30). */}
      <div className="sr-viewbar">
        <button className={`sr-viewtab${view === 'all' ? ' on' : ''}`} onClick={() => setView('all')}>
          All in One</button>
        <button className={`sr-viewtab${view === 'bysample' ? ' on' : ''}`} onClick={() => setView('bysample')}>
          By Sample</button>
      </div>

      {view === 'all' ? (
        <>
          <div className="sr-split-h" style={{ borderRadius: '4px 4px 0 0' }}>
            All Test Bottles — {bottles.length} row(s) across {samples.length} sample(s)
          </div>
          <AppDataGrid<ContainerBottle>
            rows={bottles} columns={allCols} getRowId={r => r.oid}
            loading={loading} defaultPageSize={50}
            emptyText="No bottles" />
        </>
      ) : (
        <div className="sr-split">
          <div className="sr-split-col">
            <div className="sr-split-h">Sample Details</div>
            <AppDataGrid<ContainerSample>
              rows={samples} columns={sampleCols} getRowId={r => r.oid}
              selectedRowId={activeOid}
              onRowClick={s => setActiveSample(s.sampleId)}
              showFilters={false}
              loading={loading} defaultPageSize={20}
              emptyText="No samples" />
          </div>
          <div className="sr-split-col">
            <div className="sr-split-h">
              Test Bottles — <span className="sr-split-id">{activeSample ?? '—'}</span>
            </div>
            <AppDataGrid<ContainerBottle>
              rows={rightRows} columns={editCols} getRowId={r => r.oid}
              defaultPageSize={20}
              emptyText={activeSample ? 'No bottles for this sample' : 'Select a sample to see its bottles'} />
          </div>
        </div>
      )}
    </div>
  );
}
